import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "./axiosConfig";
import { useLanguage } from "./LanguageContext"; // Import du contexte
import { 
  Upload, CheckCircle, ArrowLeft, Trash2, Edit, 
  Calendar, CreditCard, Clock, Image as ImageIcon, AlertCircle 
} from "lucide-react";

export default function MerchantSubscription() {
  const navigate = useNavigate();
  const { t } = useLanguage(); // Utilisation de la traduction

  const [payments, setPayments] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const currentYear = new Date().getFullYear();
  const MONTHS = t("months_list"); // Récupère la liste des mois traduits

  useEffect(() => {
    fetchMyPayments();
  }, []);

  const fetchMyPayments = async () => {
    try {
      const res = await api.get("/subscriptions/me");
      setPayments(res.data.payments || []);
    } catch (err) {
      console.error("Erreur chargement paiements", err);
    }
  };

  const uploadProof = async (monthOverride = null, fileOverride = null) => {
    const monthToSend = monthOverride || selectedMonth;
    const fileToSend = fileOverride || file;

    if (!monthToSend || !fileToSend) {
      alert(t("confirm_clear_all").includes("⚠️") ? "Veuillez choisir un mois et une image" : "الرجاء اختيار الشهر والصورة");
      return;
    }

    const fd = new FormData();
    // On envoie l'index du mois (1-12) pour rester cohérent avec le backend
    const monthIndex = MONTHS.indexOf(monthToSend) + 1;
    fd.append("month", monthIndex);
    fd.append("year", currentYear);
    fd.append("proof", fileToSend);

    setLoading(true);
    try {
      await api.post("/subscriptions", fd);
      setSelectedMonth("");
      setFile(null);
      fetchMyPayments();
    } catch {
      alert("Erreur / خطأ");
    } finally {
      setLoading(false);
    }
  };

  const deleteProof = async (paymentId) => {
    const ok = window.confirm(t("delete_proof_confirm"));
    if (!ok) return;

    try {
      await api.delete(`/subscriptions/${paymentId}`);
      fetchMyPayments();
    } catch {
      alert("Erreur / خطأ");
    }
  };

  const paidCount = useMemo(() => payments.length, [payments]);

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-sans text-slate-800 pb-12">
      
      {/* HEADER */}
      <header className="bg-[#f34141] py-8 px-6 rounded-b-[2.5rem] shadow-lg relative z-10">
        <div className="flex flex-col items-start justify-between max-w-6xl gap-6 mx-auto md:flex-row md:items-center">
          <div className="text-white">
            <button 
              onClick={() => navigate("/merchant/dashboard")}
              className="flex items-center gap-2 px-3 py-1.5 mb-4 text-[10px] font-black uppercase tracking-widest bg-white/10 hover:bg-white/20 rounded-lg border border-white/5 transition-all"
            >
              <ArrowLeft size={14} /> {t("back")}
            </button>
            <h1 className="flex items-center gap-3 text-3xl font-black tracking-tighter">
              <CreditCard size={28} /> {t("my_subscription")}
            </h1>
          </div>
          
          <div className="flex gap-3">
             <div className="bg-black/10 backdrop-blur-md px-5 py-3 rounded-2xl border border-white/10 text-white min-w-[120px]">
                <p className="text-[9px] uppercase font-black opacity-60 tracking-widest mb-1">{t("paid_months")}</p>
                <p className="text-2xl font-black leading-none">{paidCount} / 12</p>
             </div>
             <div className="bg-black/10 backdrop-blur-md px-5 py-3 rounded-2xl border border-white/10 text-white min-w-[120px]">
                <p className="text-[9px] uppercase font-black opacity-60 tracking-widest mb-1">{t("year")}</p>
                <p className="text-2xl font-black leading-none">{currentYear}</p>
             </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl px-4 mx-auto mt-10 space-y-10">
        
        {/* SECTION UPLOAD */}
        <section className="p-8 bg-white border shadow-sm rounded-3xl border-slate-100">
          <div className="flex items-center gap-3 mb-8">
            <div className="p-2 bg-red-50 text-[#f34141] rounded-xl">
               <Upload size={20} strokeWidth={3} />
            </div>
            <h2 className="text-sm font-black uppercase tracking-[0.2em] text-slate-400">
              {t("send_proof")}
            </h2>
          </div>

          <div className="grid items-end gap-6 md:grid-cols-3">
            <div className="flex flex-col gap-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider">{t("month_concerned")}</label>
              <select
                className="w-full px-4 py-3 border border-slate-100 rounded-xl bg-slate-50 text-sm font-bold outline-none focus:ring-2 focus:ring-[#f34141]/10 focus:border-[#f34141] transition-all"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
              >
                <option value="">{t("select_month")}</option>
                {MONTHS.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider">{t("screenshot_receipt")}</label>
              <label className="flex items-center gap-3 px-4 py-2.5 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:bg-slate-50 transition-all group">
                <ImageIcon size={20} className="text-slate-400 group-hover:text-[#f34141]" />
                <span className="text-xs font-bold truncate text-slate-500">
                  {file ? file.name : t("select_image")}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files[0])}
                />
              </label>
            </div>

            <button
              onClick={() => uploadProof()}
              disabled={loading || !file || !selectedMonth}
              className="bg-[#f34141] text-white py-3.5 rounded-xl font-black uppercase tracking-widest text-[11px] hover:bg-[#d63232] disabled:opacity-30 transition-all shadow-lg shadow-red-50 hover:scale-[1.02] active:scale-95"
            >
              {loading ? t("processing") : t("confirm_payment")}
            </button>
          </div>
        </section>

        {/* GRILLE DES MOIS */}
        <section>
          <div className="flex items-center justify-between mb-8">
            <h3 className="flex items-center gap-2 text-2xl font-black tracking-tighter text-slate-900">
              <Calendar className="text-[#f34141]" size={24} /> {t("annual_history")}
            </h3>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {MONTHS.map((month, index) => {
              const paid = payments.find(
                (p) => p.month === index + 1 && p.year === currentYear
              );

              return (
                <div 
                  key={month} 
                  className={`relative p-5 rounded-[2rem] border transition-all duration-300 ${
                    paid 
                    ? "bg-white border-slate-100 shadow-sm hover:shadow-md" 
                    : "bg-slate-50/50 border-dashed border-slate-200 opacity-80"
                  }`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-black tracking-tight text-slate-800">{month}</span>
                    {paid ? (
                      <div className="bg-emerald-50 p-1.5 rounded-full">
                        <CheckCircle className="text-emerald-500" size={16} strokeWidth={3} />
                      </div>
                    ) : (
                      <Clock className="text-slate-300" size={16} />
                    )}
                  </div>

                  {paid?.proofImage ? (
                    <div className="space-y-4">
                      <div className="relative overflow-hidden border group rounded-2xl aspect-video bg-slate-100 border-slate-50">
                        <img
                          src={paid.proofImage.startsWith("http") ? paid.proofImage : `https://souqi-api.onrender.com${paid.proofImage}`}
                          alt={`Preuve ${month}`}
                          className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 flex items-center justify-center transition-opacity opacity-0 bg-black/40 group-hover:opacity-100">
                           <span className="text-[10px] text-white font-black uppercase tracking-widest">{t("view_receipt")}</span>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <label className="flex-1 flex items-center justify-center gap-2 py-2 bg-slate-50 hover:bg-slate-100 text-slate-500 rounded-xl cursor-pointer transition-colors font-black text-[9px] uppercase tracking-widest">
                          <Edit size={12} />
                          {t("edit")}
                          <input
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={(e) => uploadProof(month, e.target.files[0])}
                          />
                        </label>

                        <button
                          onClick={() => deleteProof(paid._id)}
                          className="px-3 py-2 transition-all text-slate-300 hover:text-red-600 hover:bg-red-50 rounded-xl"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-8 border border-dashed border-slate-200 rounded-2xl">
                       <AlertCircle className="mb-2 text-slate-200" size={20} />
                       <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t("not_settled")}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

      </div>
    </div>
  );
}
